import React from 'react';
import {defineMessages} from 'react-intl';
import type {
    DateRangeFilterValue,
    FilterGroupNewFilter,
    FilterProps,
    FiltersProps,
    MultiKeyFilterTextValue,
    QueryFilterAdapterModel,
    QueryFilterAdapterProps,
} from '@igp/shared/components/filter';
import {DateRangeFilter, FilterGroupNew, TextInputWithDropdownFilter, withQueryFilter} from '@igp/shared/components/filter';
import {useLocale} from '@igp/shared/localization';
import type {Locale} from '@igp/shared/style';

import type {
    DebtTransferLogFilterKeys,
    DebtTransferLogFilterName,
    DebtTransferLogFiltersViewModel,
    DebtTransferLogTextFilterKeys,
} from '../types';

const localized = defineMessages({
    agent_id: {
        id: 'DebtTransferLogFilters_agent_id',
        defaultMessage: 'Agent ID',
    },
    master_agent_id: {
        id: 'DebtTransferLogFilters_master_agent_id',
        defaultMessage: 'Master Agent ID',
    },
    placeholder: {
        id: 'DebtTransferLogFilters_placeholder',
        defaultMessage: 'Search in Debt Transfer Logs',
    },
});

function DebtTransferLogFilters(props: Omit<FiltersProps<DebtTransferLogFiltersViewModel, DebtTransferLogFilterName>, 'submitButton'>) {
    const {locale} = useLocale();

    const filters: FilterGroupNewFilter<DebtTransferLogFiltersViewModel, DebtTransferLogFilterName>[] = [
        {
            filterName: 'text',
            modelField: 'text',
            component: (props: FilterProps<MultiKeyFilterTextValue<DebtTransferLogTextFilterKeys>>) => (
                <TextInputWithDropdownFilter
                    options={[
                        {label: localized.master_agent_id, value: 'master_agent_uid'},
                        {label: localized.agent_id, value: 'agent_uid'},
                    ]}
                    inputPlaceholder={localized.placeholder}
                    width="full"
                    {...props}
                />
            ),
            collapseOnMobile: false,
        },
        {
            filterName: 'transferDate',
            modelField: 'transferDate',
            component: (props: FilterProps<DateRangeFilterValue>) => <DateRangeFilter {...props} width="full" locale={locale as Locale} />,
        },
    ];

    return <FilterGroupNew mode="update-on-change" allFilters={filters} {...props} />;
}

function DebtTransferLogFiltersAdapter({
    model,
    onChange,
    ...props
}: Omit<QueryFilterAdapterProps<DebtTransferLogFilterKeys, DebtTransferLogFilterName, DebtTransferLogTextFilterKeys>, 'submitButton'>) {
    const filterViewModel: DebtTransferLogFiltersViewModel = {
        text: model?.text,
        transferDate: {
            from: model?.['transfer_date.from'] as number,
            to: model?.['transfer_date.to'] as number,
        },
    };

    function handleChange(model: DebtTransferLogFiltersViewModel) {
        const result: QueryFilterAdapterModel<DebtTransferLogFilterKeys, DebtTransferLogTextFilterKeys> = {
            text: model?.text,
            'transfer_date.from': model?.transferDate?.from,
            'transfer_date.to': model?.transferDate?.to,
        };
        onChange(result);
    }
    return <DebtTransferLogFilters model={filterViewModel} onChange={handleChange} {...props} />;
}

export const DebtTransferLogQueryFilter = withQueryFilter<
    DebtTransferLogFilterKeys,
    DebtTransferLogFilterName,
    DebtTransferLogTextFilterKeys
>(DebtTransferLogFiltersAdapter, {
    text: ['master_agent_uid', 'agent_uid'],
});
